import React, { useCallback, useEffect, useState } from "react";
import SwapService from "../../services/Swap/swapService";
import { statusLabel } from "./statusLabels";

const SIDE_LABEL = {
  buy: "Compra",
  sell: "Venda",
};

// Rótulos de status (Estados do SwapStatus, Higher-API,
// src/Higher.Domain/Entities/Swap/SwapEnums.cs, mesmo rótulo usado no painel
// de mesa do erp em src/components/mesa/swap-desk-panel.tsx) moraram aqui
// primeiro; agora vivem em `./statusLabels` porque `QuoteCard.jsx` também
// precisa deles (Task 28) — ver justificativa lá. `statusLabel` chamada
// abaixo se comporta exatamente como antes.

function sideLabel(side) {
  return SIDE_LABEL[String(side).toLowerCase()] || side;
}

/**
 * Histórico de operações de swap do cliente autenticado
 * (`GET /v1/baas/swap/operations`). Nas operações concluídas, mostra o hash
 * de liquidação com botão de copiar.
 */
function OperationHistory({ email, refreshToken }) {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");

  const loadOperations = useCallback(async () => {
    if (!email) return;

    setLoading(true);
    setError("");
    try {
      const response = await SwapService.listOperations(email);
      setOperations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError("Não foi possível carregar o histórico de operações.");
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    loadOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOperations, refreshToken]);

  const copyHash = (hash, id) => {
    if (!hash || !navigator.clipboard) return;
    navigator.clipboard.writeText(hash).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 2000);
    });
  };

  return (
    <section className="swapCard" id="swapOperationHistory">
      <p className="swapCard__title">Histórico de operações</p>

      {loading && <p className="swapEmpty">Carregando...</p>}
      {!loading && error && <p className="swapError">{error}</p>}
      {!loading && !error && operations.length === 0 && (
        <p className="swapEmpty">Nenhuma operação encontrada.</p>
      )}

      {!loading && !error && operations.length > 0 && (
        /* Rolagem no CONTÊINER: são seis colunas numa tela de celular, e rolar a
           página inteira na horizontal quebra o resto do app. */
        <div className="swapTableWrap">
          <table className="swapTable">
            <thead>
              <tr>
                <th>Par</th>
                <th>Lado</th>
                <th>Quantidade</th>
                <th>Preço</th>
                <th>Status</th>
                <th>Hash</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((op) => (
                <tr key={op.id}>
                  <td>{op.pair}</td>
                  <td>{sideLabel(op.side)}</td>
                  <td>{op.quantity}</td>
                  <td>{Number(op.price).toFixed(2)}</td>
                  <td>
                    {/* A cor do status sai do MESMO vocabulário de
                        statusLabels: concluída em verde, encerrada sem
                        liquidar em vermelho, dúvida em âmbar, o resto neutro.
                        Status novo do backend cai no neutro em vez de sumir. */}
                    <span className={"swapPill" + pillModifier(op.status)}>
                      {statusLabel(op.status)}
                    </span>
                  </td>
                  <td>
                    {op.status === "Completed" && op.hash ? (
                      <button
                        type="button"
                        className="swapBtn swapBtn--link"
                        onClick={() => copyHash(op.hash, op.id)}
                      >
                        {copiedId === op.id ? "Copiado!" : "Copiar hash"}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


/**
 * Cor do selo de status. Neutro é o DEFAULT de propósito: quando o backend
 * ganhar um status novo, ele aparece cinza em vez de desaparecer da tela — o
 * mesmo princípio que levou `statusLabels.js` a existir como vocabulário único.
 */
function pillModifier(status) {
  if (status === "Completed") return " swapPill--ok";
  if (status === "Expired" || status === "Cancelled") return " swapPill--danger";
  if (status === "LockUncertain") return " swapPill--warn";
  return "";
}

export default OperationHistory;
